import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FirebaseService } from '../../_services/firebase.service';
import { TemplateVariable } from '../../_interfaces/email.interface';
import { isRtlLanguage } from '../../_datas/languages.data';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface LanguageOption {
  languageCode: string;
  languageName: string;
  isValid: boolean;
  hasContent: boolean;
}

@Component({
  selector: 'app-test-email-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './test-email-dialog.component.html',
  styleUrl: './test-email-dialog.component.scss',
})
export class TestEmailDialogComponent {
  testForm: FormGroup;
  sendingEmail = false;
  languages: LanguageOption[];
  templateVariables: TemplateVariable[];
  selectedLangCode: string;
  private unSubscribe = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<TestEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      templateName: string;
      languages: LanguageOption[];
      templateVariables: TemplateVariable[];
      defaultEmail: string;
      defaultLangCode: string;
      // Source determines how test email is sent:
      // 'editor' = Always use raw HTML content (for testing drafts/unpublished templates)
      // 'published' = Always use AWS SES template (for testing production templates)
      source: 'editor' | 'published';
      // Required when source is 'editor'
      htmlPart?: { [langCode: string]: string };
      subjectPart?: { [langCode: string]: string };
    },
    private fbs: FirebaseService,
    private snackBar: MatSnackBar
  ) {
    this.languages = data.languages;
    this.templateVariables = data.templateVariables || [];

    // Find the first valid language - prefer defaultLangCode, then 'en', then first valid
    const validLanguages = this.languages.filter(l => l.isValid);
    const preferredLang = validLanguages.find(l => l.languageCode === data.defaultLangCode)
      || validLanguages.find(l => l.languageCode === 'en')
      || validLanguages[0];

    this.selectedLangCode = preferredLang?.languageCode || data.defaultLangCode || 'en';

    // Build form with pre-filled email
    this.testForm = this.fb.group({
      email: [data.defaultEmail, [Validators.required, Validators.email]],
      langCode: [this.selectedLangCode, Validators.required],
      variables: this.fb.array(
        this.templateVariables.map((v) =>
          this.fb.group({
            name: [v.name],
            value: [v.value?.[this.selectedLangCode] || v.value?.['en'] || ''],
          })
        )
      ),
    });
  }

  get variablesArray(): FormArray {
    return this.testForm.get('variables') as FormArray;
  }

  // Switch to a different language
  selectLanguage(langCode: string) {
    const lang = this.languages.find(l => l.languageCode === langCode);
    if (!lang || !lang.isValid) return;

    this.selectedLangCode = langCode;
    this.testForm.patchValue({ langCode });

    // Update variable default values when language changes
    const variablesArray = this.testForm.get('variables') as FormArray;
    this.templateVariables.forEach((v, i) => {
      variablesArray.at(i).patchValue({
        value: v.value[langCode] || v.value['en'] || '',
      });
    });
  }

  // Check if a language is RTL (for template binding)
  isRtlLanguage(code: string): boolean {
    return isRtlLanguage(code);
  }

  // Get tooltip for language button
  getLanguageTooltip(lang: LanguageOption): string {
    if (!lang.hasContent) return `${lang.languageName} - No content available`;
    if (!lang.isValid) return `${lang.languageName} - Has validation errors`;
    return `${lang.languageName} - Ready to send`;
  }

  // Check if any language is valid
  get hasAnyValidLanguage(): boolean {
    return this.languages.some(l => l.isValid);
  }

  async sendTestEmail() {
    if (this.testForm.invalid || this.sendingEmail) return;

    this.sendingEmail = true;
    const { email, langCode, variables } = this.testForm.value;

    // Build template data from variables
    const templateData: Record<string, string | number> = {};
    variables.forEach((v: { name: string; value: string }) => {
      templateData[v.name] = v.value;
    });

    const emailRequest = {
      recipient: {
        email,
        variables: templateData,
        langCode,
      },
      templateName: `${this.data.templateName}-${langCode}`,
      templateVariables: templateData,
      // Explicit source flag determines behavior:
      // 'editor' = use raw HTML content, 'published' = use SES template
      source: this.data.source,
      // Only include HTML content when source is 'editor'
      htmlContent: this.data.source === 'editor' ? this.data.htmlPart?.[langCode] : undefined,
      subjectLine: this.data.source === 'editor' ? this.data.subjectPart?.[langCode] : undefined,
    };

    this.fbs
      .sendEmail(emailRequest)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe({
        next: (res: any) => {
          this.sendingEmail = false;
          if (res?.MessageId) {
            this.snackBar.open(`✓ Test email sent to ${email}`, 'Close', {
              duration: 5000,
              panelClass: ['snackbar-success'],
            });
            this.dialogRef.close(true);
          } else {
            this.snackBar.open(
              `✗ ${res?.message || 'Failed to send email'}`,
              'Close',
              {
                duration: 5000,
                panelClass: ['snackbar-error'],
              }
            );
          }
        },
        error: (err: any) => {
          this.sendingEmail = false;
          const errorMessage = err?.error?.message || err?.message || 'Failed to send email';
          this.snackBar.open(`✗ ${errorMessage}`, 'Close', {
            duration: 5000,
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  ngOnDestroy() {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
